import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  filterByDay,
  prepareAvailabilityData,
} from "../../../services/calendarService";
import {
  TSaveCalendarDataInput,
  TWeekDaysData,
} from "../../../store/candidate/types";
import Availabilities from "../../Candidate/Availabilities";
import Checkbox from "../Inputs/Checkbox";
import PopupModal from "../Popup";
import WeekRow from "./WeekRow";

export const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri"]; // "Sat", "Sun" - Hidden weekends
export const dayNames = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  // "Saturday", - Hidden weekend
  // "Sunday", - Hidden weekend
];

interface MyCalendarProps {
  // The candidateCalendarData prop is an object where each key is a week identifier and the value is an object containing day data.
  candidateCalendarData: TWeekDaysData;
  showAvailabilitiesmodal: boolean;
  setShowAvailabilitiesModal: Dispatch<SetStateAction<boolean>>;
  loadCalendarData?: () => void; // Optional function to reload calendar data
  handleSaveAvailabilities?: (inputData: TSaveCalendarDataInput) => void; // Optional function to save availabilities
  selectedDaysResetTrigger?: boolean;
  setSelectedDaysResetTrigger?: Dispatch<SetStateAction<boolean>>;
}

const MyCalendar: React.FC<MyCalendarProps> = ({
  candidateCalendarData,
  showAvailabilitiesmodal,
  setShowAvailabilitiesModal,
  loadCalendarData,
  handleSaveAvailabilities,
  selectedDaysResetTrigger,
  setSelectedDaysResetTrigger,
}) => {
  const isFirstRender = useRef(true);

  const [checkColumns, setCheckColumns] = useState<string[]>([]);
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<{
    [weekKey: string]: {
      [dayName: string]: { date: string; checked: boolean };
    };
  }>({});

  const getAllAcceptedJobDates = (calendarData: any): string[] => {
    const acceptedDates: string[] = [];
    for (const weekKey in calendarData) {
      const week = calendarData[weekKey];
      for (const dayName in week) {
        const dayData = week[dayName];
        if (dayData && dayData.accepted_count === 1) {
          acceptedDates.push(dayData.date);
        }
      }
    }
    return acceptedDates;
  };

  const acceptedJobDates = getAllAcceptedJobDates(candidateCalendarData);

  useEffect(() => {
    if (isFirstRender.current) {
      // When component mount the checkedState is updated and also have empty so at the time to avoid calling save API this flag added
      isFirstRender.current = false;
      return; // Skip on initial render
    }

    // Check if selectedDays is effectively empty
    if (Object.keys(selectedDays).length === 0) {
      return; // Skip API call if no day is changed
    }

    const inputData: TSaveCalendarDataInput = {
      type: "single",
      // is_available: 1,
      availabilities: prepareAvailabilityData(selectedDays, acceptedJobDates),
    };

    handleSaveAvailabilities && handleSaveAvailabilities(inputData);
  }, [selectedDays]);

  useEffect(() => {
    setSelectedDays({});
  }, [selectedDaysResetTrigger]);

  /**
   * Checks if all rows in the column for a given day are checked.
   * This function iterates through all weeks and checks the state of the specified day.
   * It returns true if all rows for that day are checked, otherwise false.
   *
   * @param dayIdx - The index of the day (0 for Monday, 6 for Sunday).
   * Checks if all rows in the column for the given day are checked.
   * @returns boolean - Returns true if all rows in the column are checked, false otherwise.
   */
  const isColumnChecked = (idx: number): boolean => {
    if (!candidateCalendarData) return false;
    const dayName = dayNames[idx]; // e.g., "Monday", "Tuesday", ...

    // Get all weeks (excluding any non-week keys)
    const weekKeys = Object.keys(candidateCalendarData).filter((key) =>
      key.startsWith("week_")
    );

    // Return true only if every week has that day and it's marked checked
    return weekKeys.every((weekKey) => {
      const week = candidateCalendarData[weekKey];
      const day = week[dayName];
      return day?.checked === true;
    });
  };

  /**
   * This function initializes the checked state for each week and day.
   * It iterates through the candidateCalendarData and sets the initial checked state
   *
   * @param dayIdx - The index of the day (0 for Monday, 6 for Sunday).
   * This function handles the bulk change of checkboxes for a specific day across all weeks.
   * @param checked - The checked state to apply to all rows for that day.
   */
  const handleBulkChangeColumn = (dayIdx: number, checked: boolean) => {
    const day = dayNames[dayIdx];
    const today = new Date();
    setCheckColumns((prev) => {
      const dayName = dayNames[dayIdx];
      if (checked) {
        // Only add if not already present
        if (!prev.includes(dayName)) {
          return [...prev, dayName];
        }
      }
      // If unchecked, remove the day from the array
      return prev.filter((d) => d !== dayName);
    });
    today.setHours(0, 0, 0, 0); // Normalize today's date to midnight
    // setCheckedState({}); // Reset the checked state before applying the new one
    setSelectedDays((prev) => {
      const newState = { ...prev };
      const filteredByDay = filterByDay(candidateCalendarData, day);
      Object.keys(filteredByDay).forEach((weekKey) => {
        if (!newState[weekKey]) newState[weekKey] = {};
        const date = candidateCalendarData[weekKey]?.[day]?.date || "";
        const dateObj = new Date(date);
        dateObj.setHours(0, 0, 0, 0); // Normalize the date to midnight
        if (dateObj >= today) {
          // Only update the state if the date is today or in the future
          newState[weekKey][day] = { date, checked };
        }
      });

      return newState;
    });
  };

  /**
   * This function handles the change of a single cell in the calendar.
   *
   * @param weekKey - The key of the week in the candidateCalendarData.
   * @param day - The name of the day (e.g., "Monday", "Tuesday").
   * @param checked - The checked state for the day.
   */
  const handleCellChange = (weekKey: string, day: string, checked: boolean) => {
    const date = candidateCalendarData[weekKey]?.[day]?.date || "";
    setSelectedDays((prev) => ({
      ...prev,
      [weekKey]: {
        ...prev[weekKey],
        [day]: { date, checked },
      },
    }));
  };

  const handleBulkChangeRow = (weekKey: string, checked: boolean) => {
    // setSelectedDaysResetTrigger && setSelectedDaysResetTrigger(true); // Reset the trigger to true before making changes
    setCheckedRows((prev) => {
      if (checked) {
        // Only add if not already present
        if (!prev.includes(weekKey)) {
          return [...prev, weekKey];
        }
      }
      // If unchecked, remove the day from the array
      return prev.filter((d) => d !== weekKey);
    });
    const week = candidateCalendarData[weekKey];
    if (!week) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today's date to midnight
    // Save both day name and date
    const newCheckedDays: {
      [dayName: string]: { date: string; checked: boolean };
    } = {};
    Object.entries(week).forEach(([dayName, dayData]: [string, any]) => {
      const dateObj = new Date(dayData.date);
      dateObj.setHours(0, 0, 0, 0); // Normalize the date to midnight
      if (dayData && dayData.date && dateObj >= today) {
        newCheckedDays[dayName] = { date: dayData.date, checked };
      }
    });
    setSelectedDays((prev) => ({
      [weekKey]: newCheckedDays,
    }));
  };

  const onClickCloseAvailability = () => {
    setShowAvailabilitiesModal(false);
  };

  const onRefresh = () => {
    loadCalendarData && loadCalendarData();
  };

  const isColumnDisabled = (dayIdx: number): boolean => {
    if (!candidateCalendarData) return false;

    const dayName = dayNames[dayIdx];
    const filteredByDay = filterByDay(candidateCalendarData, dayName);
    // Check if any week has accepted_count > 0 for the given day
    return Object.values(filteredByDay).some((weekData) => {
      const dayData = weekData[dayName];
      return dayData?.accepted_count > 0;
    });
  };

  return (
    <>
      <PopupModal
        popUpClass="mobile-model"
        visible={showAvailabilitiesmodal}
        showCloseBtn={true}
        onClickClose={onClickCloseAvailability}
      >
        <Availabilities
          setShowAvailabilitiesModal={setShowAvailabilitiesModal}
          checkedState={selectedDays}
          loadCalendarData={loadCalendarData}
        />
      </PopupModal>

      <div className="table-responsive">
        <table className="table table-bordered text-center border-grey-light">
          <thead>
            <tr>
              <th
                className="bg-white"
                style={{
                  borderLeftColor: "white",
                  borderTop: "1px solid white",
                  marginTop: "-1px",
                }}
              ></th>
              {weekDays.map((day, idx) => (
                <th
                  key={day}
                  className={`${day === "Fri" ? "radius-top-right-8px" : ""}`}
                >
                  <div className="d-flex flex-column flex-lg-row align-items-center justify-content-between py-4px">
                    <div className="fs-16 fw-700 text-black d-none d-lg-block">
                      {day}
                    </div>
                    <div className="fs-16 fw-700 text-black d-block d-lg-none">
                      {day[0]}
                    </div>
                    <div className="theme-check">
                      <Checkbox
                        className="theme-check-input"
                        id={day}
                        checked={checkColumns.includes(dayNames[idx])}
                        onChange={(checked) =>
                          handleBulkChangeColumn(idx, checked)
                        }
                        label={"Bulk Change"}
                        themeCheck={true}
                        labelBold={false}
                        labelLeft={true}
                        labelFontSize="13"
                        labelSpanClass="d-none d-lg-block"
                      />
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {candidateCalendarData &&
              Object.entries(candidateCalendarData).map(
                ([weekName, weekData]: [string, any], index: number) => (
                  <WeekRow
                    key={index}
                    weekData={weekData}
                    weekName={weekName}
                    handleCellChange={handleCellChange}
                    handleBulkChangeRow={handleBulkChangeRow}
                    onRefresh={onRefresh}
                    setSelectedDaysResetTrigger={setSelectedDaysResetTrigger}
                    checkedRows={checkedRows}
                  />
                )
              )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default MyCalendar;
